import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { getFilterDropdown } from 'components/data/helpers/filters';
import { DataTableContext } from 'components/DataTable';
import { type CustomFilterDropdownProps } from 'horizon/types/TableColumnDef';
import { useGetAvailableSelectionsQuery } from 'types/atlas-graphql';

import {
  type DamageSchemaFilterColumn,
  getDamageSchemaFilterState,
  getSelectedDamageSchemaFilterValues,
} from './utils';

type Props = CustomFilterDropdownProps & {
  column: DamageSchemaFilterColumn & {
    key: string;
  };
  schemaColumns: DamageSchemaFilterColumn[];
  schemaId?: string;
};

export const ConditionalDamageSchemaFilterDropdown = ({
  column,
  schemaColumns,
  schemaId,
  selectedKeys,
  ...filterDropdownProps
}: Props) => {
  const { t } = useTranslation();
  const { filteredInfo } = useContext(DataTableContext);
  const { hasMultipleSelections, selections } = useMemo(
    () =>
      getDamageSchemaFilterState({
        columns: schemaColumns,
        currentColumnKey: column.key,
        currentSelectedKeys: selectedKeys,
        filteredInfo,
      }),
    [column.key, filteredInfo, schemaColumns, selectedKeys]
  );
  const selectedValues = useMemo(
    () => getSelectedDamageSchemaFilterValues(selectedKeys),
    [selectedKeys]
  );

  const { data, error, loading } = useGetAvailableSelectionsQuery({
    variables: {
      ...(schemaId ? { schemaId } : {}),
      selections,
    },
    skip: hasMultipleSelections,
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
  });

  const conditionalValues = useMemo(() => {
    if (hasMultipleSelections) {
      return selectedValues;
    }
    if (error || loading) {
      return selectedValues.length ? selectedValues : undefined;
    }

    const availableSelections = data?.getAvailableDamageSchemaAttributeSelections ?? [];
    const currentSelection = availableSelections.find(
      selection =>
        selection.attr.name === column.key &&
        selection.__typename === 'DamageSchemaAttributeSelectionString'
    );

    const schemaValues =
      currentSelection?.__typename === 'DamageSchemaAttributeSelectionString'
        ? currentSelection.options
        : [];

    return Array.from(new Set([...selectedValues, ...schemaValues]));
  }, [column.key, data, error, hasMultipleSelections, loading, selectedValues]);

  const SelectFilterDropdown = useMemo(
    () =>
      getFilterDropdown({
        ...column.config,
        type: 'string',
        conditionalValues,
        conditionalEmptyText: t(
          'table.noSchemaCombinationsAvailable',
          'No schema combinations available'
        ),
        conditionalLabel: t('table.suggested', 'Suggested'),
        remainingLabel: t('table.everythingElse', 'Everything Else'),
      }),
    [column.config, conditionalValues, t]
  );

  return <SelectFilterDropdown {...filterDropdownProps} selectedKeys={selectedKeys} />;
};
